import { useMemo } from 'react';
import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';

/**
 * Configurações — hub que agrupa as telas de administração/conta em abas laterais,
 * pra enxugar o menu principal. As abas marcadas como `adminOnly` só aparecem pra
 * admin; App de Docs e Console seguem visíveis a todos (como eram no menu antigo).
 * Cada aba renderiza a página existente via <Outlet> (rotas aninhadas em App.tsx).
 */

type Tab = { to: string; label: string; adminOnly?: boolean };
type TabGroup = { label: string; tabs: Tab[] };

const GROUPS: TabGroup[] = [
  {
    label: 'Configurações',
    tabs: [
      { to: '/settings/users', label: 'Usuários', adminOnly: true },
      { to: '/settings/roles', label: 'Perfis & Permissões', adminOnly: true },
      { to: '/settings/sync-app', label: 'App de Docs' },
      { to: '/settings/console', label: 'Console' },
    ],
  },
  {
    label: 'Sistema',
    tabs: [
      { to: '/settings/agent-bases', label: 'Padrões de Agente', adminOnly: true },
      { to: '/settings/onboarding-types', label: 'Tipos de Onboarding', adminOnly: true },
      { to: '/settings/usage', label: 'Uso & Custo', adminOnly: true },
      { to: '/settings/db-audit', label: 'Auditoria', adminOnly: true },
      { to: '/settings/agents', label: 'Agentes ativos', adminOnly: true },
      { to: '/settings/pipeline-md', label: 'Roteiros & Fluxos (IA)', adminOnly: true },
    ],
  },
];

export function Settings() {
  const { me } = useAuth();
  const isAdmin = me?.globalRole === 'admin';
  const loc = useLocation();

  const groups = useMemo(
    () =>
      GROUPS.map((g) => ({ ...g, tabs: g.tabs.filter((t) => !t.adminOnly || isAdmin) })).filter(
        (g) => g.tabs.length > 0,
      ),
    [isAdmin],
  );

  // /settings puro → primeira aba visível.
  if (loc.pathname === '/settings' || loc.pathname === '/settings/') {
    const first = groups[0]?.tabs[0]?.to;
    return first ? <Navigate to={first} replace /> : <div className="page">Nada por aqui.</div>;
  }

  return (
    <div className="page-fluid" style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'flex-start' }}>
      <nav
        style={{
          width: 220,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
          position: 'sticky',
          top: 'var(--space-4)',
        }}
      >
        {groups.map((g) => (
          <div key={g.label}>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--color-fg-muted)',
                margin: '0 0 var(--space-2) var(--space-3)',
              }}
            >
              {g.label}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {g.tabs.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  style={({ isActive }) => ({
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-md, 8px)',
                    fontSize: 'var(--font-size-sm)',
                    textDecoration: 'none',
                    color: isActive ? 'var(--color-fg)' : 'var(--color-fg-muted)',
                    background: isActive ? 'var(--color-bg-subtle)' : 'transparent',
                    fontWeight: isActive ? 600 : 400,
                  })}
                >
                  {t.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Outlet />
      </div>
    </div>
  );
}
